<template>
  <ViewerContent />

  <div class="flex flex-col justify-between gap-4 mt-4 md:flex-row md:items-start 2xl:max-w-6xl 2xl:mx-auto dark:text-zinc-300" v-if="!limitReached">
    <div class="grow">
      <!-- Collection Name -->
      <div class="mx-4 sm:mx-0">
        <MetaButton
          v-if="collectionCurrentItem"
          tooltip="Show collection"
          data-testid="viewer-button-collection-name"
          tooltip-placement="top-start"
          :href="collectionCurrentItem.url"
        >
          <Icon name="general.folder" size="sm" />
          <span>{{ collectionCurrentItem.name }}</span>
        </MetaButton>
      </div>

      <ItemName />

      <!-- Item Toolbar -->
      <div v-if="isCurrentItemOwner" class="mx-4 sm:mx-0 mt-1 flex gap-4 justify-start items-center text-sm dark:text-zinc-300">
        <ButtonExpires />
        <ButtonViews />
        <span class="text-slate-200 dark:text-zinc-700">|</span>
        <ButtonDownload />
        <ButtonFavorite />
        <ButtonCollection />
      </div>
    </div>
    <Actions />
  </div>

  <ItemHeader />

  <div class="flex flex-col gap-8 md:flex-row md:mt-4 2xl:max-w-6xl 2xl:mx-auto" v-if="!limitReached">
    <section
      class="flex justify-start gap-3 px-4 mt-6 shrink-0 md:order-2 sm:px-0"
      :class="{ 'md:mt-[3.99rem]': isTabListVisible, 'md:pt-4 md:mt-4': !isTabListVisible }"
      aria-labelledby="reactionsTitle"
    >
      <h2 id="reactionsTitle" class="sr-only">Reactions</h2>
      <Reactions :id="id" />
    </section>

    <ItemSubheaderTabs @addedComment="addedComment" />
  </div>
  <!-- <SignUpModalMobile /> -->
</template>

<script lang="ts" setup>
import { watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';

import ItemName from './Name.vue';
import MetaButton from './MetaButton.vue';
import Icon from '@/storybook/icon/Icon.vue';
import ViewerContent from './Content.vue';
import Actions from './Actions.vue';

import ItemHeader from './ItemHeader.vue';
import Reactions from './Reactions.vue';
import ItemSubheaderTabs from './ItemSubheaderTabs.vue';
import ButtonCollection from './meta_buttons/ButtonCollection.vue';
import ButtonDownload from './meta_buttons/ButtonDownload.vue';
import ButtonExpires from './meta_buttons/ButtonExpires.vue';
import ButtonFavorite from './meta_buttons/ButtonFavorite.vue';
import ButtonViews from './meta_buttons/ButtonViews.vue';
// import SignUpModalMobile from '@/components/SignUpModalMobile.vue';

import { useUser } from '@/modules/viewer/composables/useUser';
import { SubheaderTabs, useSubheaderTabs } from '../composables/useSubheaderTabs';
import { useTrack, TrackEvents, TrackClickEvents } from '../../../composables/useTrack';

const { isTabListVisible, selectedTab } = useSubheaderTabs();
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useAuthModals } from '@/composables/useAuthModals';
import { useUserStore } from '@/store/useUserStore';
import { useFeatures } from '@/composables/useFeatures';
import { useSettingsStore } from '@/store/useSettingsStore';

const store = useViewerItemStore();
const { id, limitReached, collectionCurrentItem, hideBranding } = storeToRefs(store);
const { showSignUpModal } = useAuthModals();
const { getItemFeature } = useFeatures();
const { secondaryBaseDomain, isCustomDomain } = storeToRefs(useSettingsStore());

const { isCurrentItemOwner } = useUser();
const { track, trackItemOwner, trackClick } = useTrack();
const userStore = useUserStore();
const showSignupModalBeta = getItemFeature('web-experiment-logged-out-signup', false);

onMounted(() => {
  // if the origin domain is not share.getcloudapp.com proceed with signup modal logic
  if (!(secondaryBaseDomain?.value && origin.search(secondaryBaseDomain.value) > -1)) {
    if (!userStore.isLoggedIn && showSignupModalBeta.value && !hideBranding.value && !isCustomDomain?.value) {
      showSignUpModal({
        showModalForFreeTier: true,
        signedUpFrom: 'viewer_modal_logged_out',
      });
    }
  }
});

watch(selectedTab, newValue => {
  if (newValue === SubheaderTabs.TRANSCRIPTIONS) {
    trackItemOwner(TrackEvents.VIEWER_TRANSCRIPTION_VIEWED);
  }

  if (newValue === SubheaderTabs.ANALYTICS) {
    trackClick(TrackClickEvents.VIEWER_ANALYTICSTAB);
  }
});

async function addedComment() {
  if (userStore.isLoggedIn || hideBranding.value) {
    return;
  }

  await track(TrackEvents.PROMPT_SIGN_UP_COMMENT);
  await showSignUpModal({ sidebarText: 'Join Zight to add your name to the conversation' });
}
</script>
